import React, { useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import { useNavigationProject } from '@atlassian/jira-business-entity-project/src/services/navigation-project/index.tsx';
import ShortcutMenuContentAsyncOld from '@atlassian/jira-business-shortcut/src/ui/shortcut-menu-content/async.tsx';
import { useShortcutItems } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/state-container/index.tsx';
import { useFeatures } from '@atlassian/jira-navigation-apps-sidebar-core/src/controllers/index.tsx';
import type { HideNavigationItemArgs } from '@atlassian/jira-navigation-kit-hide-item-service-old/src/services/index.tsx';
import Tab from '@atlassian/jira-navigation-kit-tab-old/src/ui/index.tsx';
import ShortcutsApp from '@atlassian/jira-navigation-shortcuts/src/index.tsx';
import type { NavigationItem } from '@atlassian/jira-navigation-types/src/types.tsx';
import type { JiraNavigationItemTypeKey } from '@atlassian/jira-relay/src/__generated__/simpleNavigationTab_businessTabs.graphql';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import type { shortcutNavigationTab_businessTabs_OldShortcutNavigationTab$key } from '@atlassian/jira-relay/src/__generated__/shortcutNavigationTab_businessTabs_OldShortcutNavigationTab.graphql';
import { TabIcon } from '../../../common/ui/tab-icon/index.tsx';

type Props = {
	tabFragment: shortcutNavigationTab_businessTabs_OldShortcutNavigationTab$key;
	id: string;
	position: number;
	canEdit?: boolean;
	isSelected?: boolean;
	onHideTab: (args: Pick<HideNavigationItemArgs, 'id' | 'relayId'>) => void;
	onTabClick: (itemTypeKey: JiraNavigationItemTypeKey, extra: { childCount: number }) => void;
	handleDrag?: () => void;
	beforeItemId: string | null;
	afterItemId: string | null;
};

/**
 * @deprecated delete when cleaning up getWillShowNav4
 */
const OldShortcutNavigationTab = ({
	tabFragment,
	position,
	id,
	canEdit,
	onHideTab,
	onTabClick,
	handleDrag,
	beforeItemId,
	afterItemId,
	isSelected = false,
}: Props & {
	tabFragment: shortcutNavigationTab_businessTabs_OldShortcutNavigationTab$key;
}) => {
	const [{ data: shortcutItems }, actions] = useShortcutItems();
	const { project } = useNavigationProject();
	const { data: featuresData } = useFeatures();
	const isAdmin = useIsAdmin();

	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState<boolean>(false);

	const tabData = useFragment(
		graphql`
			fragment shortcutNavigationTab_businessTabs_OldShortcutNavigationTab on JiraNavigationItem {
				...ui_navigationKitTabOld_Tab
				__typename
				id
				__id
				canRemove
			}
		`,
		tabFragment,
	);

	const canHideShortcuts = Boolean(tabData.canRemove);
	const canAddShortcuts = Boolean(featuresData?.hasAddItem);

	if (!project) {
		return null;
	}

	const handleDeleteView = () => {
		onHideTab({
			id: tabData?.id ?? null,
			relayId: tabData?.__id ?? null,
		});
	};

	const trigger = ({
		isSelected: isMenuOpen,
		onClick,
		triggerRef,
		...triggerProps
	}: CustomTriggerProps) => (
		<Tab
			tabFragment={tabData}
			type="dropdown-trigger"
			isSelected={isMenuOpen || isSelected}
			elemBefore={<TabIcon typeKey="SHORTCUTS" label="" size="small" />}
			onClick={(event: React.MouseEvent) => {
				onClick && onClick(event);
				onTabClick('SHORTCUTS', { childCount: shortcutItems.length });
			}}
			position={position}
			id={id}
			ref={triggerRef}
			handleDrag={handleDrag}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
			canEdit={canEdit}
			{...triggerProps}
		/>
	);

	return (
		// ShortcutsApp expects items type to be NavigationItem, when useShortcutItems returns items with type ShortcutItem
		<ShortcutsApp
			// @ts-expect-error - TS2322
			methods={actions}
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			items={shortcutItems as NavigationItem[]}
			projectKey={project.key}
			projectId={project.id}
			isAdmin={isAdmin}
			canAddLinks={canAddShortcuts}
			canAddRepositories={false}
			baseUrl=""
		>
			{({ openAddModal, openEditModal }) => (
				<DropdownMenu
					trigger={trigger}
					placement="bottom-end"
					shouldFlip={false}
					isOpen={isDropdownMenuOpen}
					testId="business-tabs.ui.simple-navigation-tab.shortcut-navigation-tab.dropdown-menu"
					onOpenChange={() =>
						setIsDropdownMenuOpen((isDropdownMenuCurrent) => !isDropdownMenuCurrent)
					}
				>
					<ShortcutMenuContentAsyncOld
						canAddShortcuts={canAddShortcuts}
						onOpenAddModal={openAddModal}
						onOpenEditModal={openEditModal}
						onClose={() => setIsDropdownMenuOpen(false)}
						onHideShortcuts={handleDeleteView}
						canHideShortcuts={canHideShortcuts}
					/>
				</DropdownMenu>
			)}
		</ShortcutsApp>
	);
};

export const ShortcutNavigationTab = OldShortcutNavigationTab;
